import { GetDeviceSnapshotDTO, UnitStateV2 } from '@eppendorf/vnls-device-service-utils';
import { IconSizeClasses } from '@eppendorf/vnls-react-components';
import type { ReactElement } from 'react';

import { BatteryStatus } from '$components/device/battery-status';
import { SignalStrengthIcon } from '$components/device/signal-strength-icon';
import { DoorOrLidStatusWrapper } from '$components/door-or-lid-status/door-or-lid-status-wrapper';
import { SidecarRouterOutlet } from '$components/sidecar-router/sidecar-router-outlet';
import { UnlessSenseMonitor } from '$components/unless-sense-monitor';
import { ConnectionStatusBadge } from '$features/devices/connection-status-badge';
import { DeviceDetailHeader } from '$features/devices/device-detail-header';
import { useDeviceViewConfig } from '$features/devices/devices.view.config';
import { ActivityStatusWrapperSnapshot } from '$features/monitoring/activity-status/activity-status-wrapper-snapshot';
import { UnitAlertState } from '$features/monitoring/alert-status/unit-alert-state';
import { CentrifugeLargeRotorInfo } from '$features/monitoring/detail/centrifuge-large-rotor-info';
import { MonitoringDetailNavigation } from '$features/monitoring/detail/monitoring-detail-navigation';
import { UnlessOffline } from '$features/monitoring/unless-offline';

export interface MonitoringDetailProps {
  snapshot?: GetDeviceSnapshotDTO;
}

function getBatteryFromSnapshot(snapshot?: GetDeviceSnapshotDTO) {
  const batterySocComponent = snapshot?.currentShadow.components.find(
    (c) => c.id === 'batterySoc',
  );
  const batterySoc = Number(batterySocComponent?.state);
  const batterySocNum = Number.isNaN(batterySoc) ? undefined : batterySoc;
  const batteryChargingComponent = snapshot?.currentShadow.components.find(
    (c) => c.id === 'batteryCharging',
  );
  const batteryCharging = Boolean(batteryChargingComponent?.state);

  return {
    soc: batterySocNum,
    charging: batteryCharging,
  };
}

function getSignalStrengthFromSnapshot(snapshot?: GetDeviceSnapshotDTO) {
  const signalStrengthComponent = snapshot?.currentShadow.components.find(
    (c) => c.id === 'signalStrength',
  );
  const signalStrength = Number(signalStrengthComponent?.state);
  return Number.isNaN(signalStrength) ? undefined : signalStrength;
}

export function MonitoringDetail({ snapshot }: MonitoringDetailProps): ReactElement {
  const viewConfig = useDeviceViewConfig(snapshot?.inventoryDevice);

  const batteryStatus = getBatteryFromSnapshot(snapshot);

  const signalStrength = getSignalStrengthFromSnapshot(snapshot);

  return (
    <div className="sidecar__layout" data-testid="device-detail-header">
      <div className="sidecar__masthead">
        <DeviceDetailHeader
          className="sidecar__device-detail-header"
          device={snapshot?.inventoryDevice}
        />
        <div className="sidecar__device-states-group">
          <div className="sidecar__device-states">
            <ActivityStatusWrapperSnapshot
              withDateWhenOffline
              bordered
              deviceShadow={snapshot?.currentShadow}
              fallback={
                <ConnectionStatusBadge
                  bordered
                  status={snapshot?.inventoryDevice.connectionStatus}
                />
              }
            />
          </div>
          {viewConfig.showBattery &&
            snapshot &&
            snapshot.currentShadow.activityState !== UnitStateV2.Offline && (
              <BatteryStatus
                soc={batteryStatus?.soc}
                charging={batteryStatus?.charging}
                className="sidecar__device-battery-state"
              />
            )}

          {viewConfig.showSignalStrength &&
            snapshot &&
            snapshot.currentShadow.activityState !== UnitStateV2.Offline && (
              <div className="sidecar__device-signal-strength">
                <SignalStrengthIcon
                  signalStrength={signalStrength ?? null}
                  className="icon--signal-strength"
                  size={IconSizeClasses.Responsive}
                />
              </div>
            )}
          <div className="sidecar__alert-state">
            <UnlessSenseMonitor deviceModel={snapshot?.inventoryDevice.model} fallback="">
              <UnlessOffline
                activityState={snapshot?.currentShadow?.activityState}
                noFallback
              >
                <UnitAlertState
                  aggregateAlertState={snapshot?.currentShadow?.aggregateAlertState}
                  side="bottom"
                />
                <DoorOrLidStatusWrapper shadow={snapshot?.currentShadow} fallback="" />
              </UnlessOffline>
            </UnlessSenseMonitor>
          </div>
          <CentrifugeLargeRotorInfo snapshot={snapshot} />
        </div>
      </div>
      <div className="sidecar__details box box-remove-padding">
        <div className="sidecar__tabs-navigation">
          <MonitoringDetailNavigation snapshot={snapshot} />
        </div>
        <div className="sidecar__outlet-wrapper">
          <SidecarRouterOutlet />
        </div>
      </div>
    </div>
  );
}
